// src/BusinessTrends.js — what the numbers are doing, not just what they are.
//
// The dashboard showed totals: so many bookings, so many profiles. A total
// cannot answer the question an owner actually has, which is whether this week
// is better than last. These are the same figures over time, from
// admin_daily_metrics.
//
// Read with the admin's own permissions — the view carries security_invoker, so
// there is no privileged back door here.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, ToggleButton, ToggleButtonGroup,
  CircularProgress, Alert, Chip,
} from '@mui/material';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { supabase } from './supabaseClient';

const SERIES = [
  { key: 'bookings_made', label: 'Bookings made', colour: '#B8912F' },
  { key: 'subscriptions_started', label: 'Subscriptions started', colour: '#2E7D32' },
  { key: 'try_ons', label: 'Virtual try-ons', colour: '#1565C0' },
  { key: 'people_joined', label: 'People joined', colour: '#6A1B9A' },
  { key: 'closet_items_added', label: 'Closet items added', colour: '#C2185B' },
];

const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

/// Compares the chosen window against the one before it, so "up" and "down"
/// mean something. Returns null when there is no earlier window to compare to —
/// an honest blank rather than a fabricated 0%.
function trend(rows, key, days) {
  if (rows.length < days * 2) return null;
  const recent = rows.slice(-days).reduce((n, r) => n + Number(r[key] || 0), 0);
  const previous = rows.slice(-days * 2, -days).reduce((n, r) => n + Number(r[key] || 0), 0);
  if (previous === 0) return recent === 0 ? null : { pct: null, recent, previous };
  return { pct: Math.round(((recent - previous) / previous) * 100), recent, previous };
}

export default function BusinessTrends() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from('admin_daily_metrics')
        .select('*')
        .order('day', { ascending: true });
      if (queryError) throw queryError;
      setRows(data || []);
    } catch (e) {
      setError(e.message || 'Could not load the trends');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => rows.slice(-days).map((r) => ({
    ...r,
    label: new Date(`${r.day}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
  })), [rows, days]);

  const totals = useMemo(() => SERIES.map((s) => ({
    ...s,
    total: shown.reduce((n, r) => n + Number(r[s.key] || 0), 0),
    change: trend(rows, s.key, days),
  })), [shown, rows, days]);

  const nothingYet = shown.length > 0 && totals.every((t) => t.total === 0);

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Box>
          <Typography variant="h6">Trends</Typography>
          <Typography variant="body2" color="textSecondary">
            Bookings, subscriptions and activity over time.
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small" exclusive value={days}
          onChange={(_e, v) => { if (v) setDays(v); }}
        >
          {RANGES.map((r) => (
            <ToggleButton key={r.days} value={r.days}>{r.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {totals.map((t) => (
          <Chip
            key={t.key}
            sx={{ borderColor: t.colour, color: t.colour }}
            variant="outlined"
            label={
              `${t.label}: ${t.total}` +
              // No arrow when there is nothing to compare against. A "0%" on a
              // business with no history reads as flat when the truth is unknown.
              (t.change && t.change.pct !== null
                ? `  ${t.change.pct >= 0 ? '▲' : '▼'} ${Math.abs(t.change.pct)}%`
                : '')
            }
          />
        ))}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : nothingYet ? (
        <Alert severity="info">
          Nothing happened in this window yet. The chart fills in as bookings, subscriptions
          and try-ons come through.
        </Alert>
      ) : (
        <Box sx={{ width: '100%', height: 320 }}>
          <ResponsiveContainer>
            <AreaChart data={shown} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
              <defs>
                {SERIES.map((s) => (
                  <linearGradient key={s.key} id={`g-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={s.colour} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={s.colour} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} minTickGap={24} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {SERIES.map((s) => (
                <Area
                  key={s.key} type="monotone" dataKey={s.key} name={s.label}
                  stroke={s.colour} fill={`url(#g-${s.key})`} strokeWidth={2}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
}
